import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FiltroPecas, ItemPeca } from '../../../interfaces/PrevisaoPecas';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import SearchIcon from '../../../img/search-icon.svg';
import FilterIcon from '../../../img/filter.svg';
import FiltroPrevisaoModal, { FILTROS_DE_PREVISAO_PADRAO, FiltrosDePrevisao } from '../components/FiltroPrevisaoModal';
import PrevisaoItemModal from '../components/PrevisaoItemModal';
import SincronizacaoPrevisao from '../components/SincronizacaoPrevisao';
import AbaPedidos from '../components/AbaPedidos';
import AbaPecas from '../components/AbaPecas';
import { PREVISAO_PECAS_PEDIDO } from '../routes';

import '../../recepcao/pages/Recepcao.css';
// O botão da linha (alcançável por teclado) — o mesmo do Rastreamento.
import '../../rastreamento/pages/RastreamentoPage.css';
import '../../../shared/ui/dashboard-base.css';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/badge-status.css';
import '../../../shared/ui/sub-abas.css';

type AbaDeTopo = 'PEDIDOS' | 'PECAS' | 'SINCRONIZACAO';

/** O card branco da tabela — o mesmo desenho da Recepção (sem classe própria). */
const estiloDoCardDaTabela = (comPaginacao: boolean): React.CSSProperties => ({
  width: '100%',
  paddingTop: 30,
  paddingBottom: comPaginacao ? 0 : 30,
  background: 'var(--tema-superficie, white)',
  borderRadius: 16,
  border: '1px var(--tema-linha, #CACACA) solid',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  display: 'flex',
  marginBottom: 20,
});

/**
 * Previsão de Peças (previsao-pecas/at-02) — o acompanhamento da chegada das peças compradas.
 *
 * A chegada é POR PEÇA, com quantidade, e vem sozinha das notas de entrada do Nasajon: a nota diz
 * quais produtos e quanto chegou, e o sistema liga cada um à peça do pedido. A pessoa entra em dois
 * casos — a peça que chegou antes da nota, e o item da nota que não casou com nenhuma peça. Os dois
 * acontecem na tela do pedido (`PedidoPage`).
 *
 * Três abas: **Pedidos** (inicial, porque é pelo pedido que a chegada entra no ERP), **Peças** (a
 * antiga aba Itens) e **Sincronização**.
 *
 * O TI vê a tela em leitura (decisão do Victor, 24/10/2026): os botões de escrita dependem do papel.
 */
const PrevisaoPecasPage: React.FC = () => {
  const navigate = useNavigate();

  const [abaDeTopo, setAbaDeTopo] = useState<AbaDeTopo>('PEDIDOS');
  const [buscaInput, setBuscaInput] = useState('');
  const [busca, setBusca] = useState('');
  const [filtros, setFiltros] = useState<FiltrosDePrevisao>(FILTROS_DE_PREVISAO_PADRAO);
  const [filtroAberto, setFiltroAberto] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [subAbaDePecas, setSubAbaDePecas] = useState<FiltroPecas>('');
  /*
   * Esta tela não grava nada (registro e cancelamento de chegada vivem na tela do pedido), então as
   * abas não precisam de sinal de recarga: `versao` fica fixo.
   */
  const versao = 0;

  const [itemAberto, setItemAberto] = useState<ItemPeca | null>(null);

  const filtrosDaConsulta = useMemo(
    () => ({
      busca: busca || undefined,
      emissaoDe: filtros.emissaoDe || undefined,
      emissaoAte: filtros.emissaoAte || undefined,
      oficinaExterna: filtros.oficinaExterna || undefined,
    }),
    [busca, filtros],
  );
  const filtrosDasPecas = useMemo(
    () => ({
      ...filtrosDaConsulta,
      recebidoDe: filtros.recebidoDe || undefined,
      recebidoAte: filtros.recebidoAte || undefined,
    }),
    [filtrosDaConsulta, filtros],
  );

  const buscar = () => setBusca(buscaInput.trim());
  const abrirPedido = useCallback((id: string) => navigate(PREVISAO_PECAS_PEDIDO(id)), [navigate]);
  const abrirItem = useCallback((item: ItemPeca) => setItemAberto(item), []);

  const secoesDaAjuda: ComoFuncionaSecao[] = [
    {
      titulo: '1. De onde vem a chegada',
      conteudo: (
        <p>
          Quando a nota fiscal de entrada é lançada no Nasajon, ela diz <strong>quais peças chegaram e quantas</strong>.
          O sistema lê a nota e liga cada peça dela à peça do pedido. Um pedido que chega em três entregas tem três
          notas, e cada uma aparece com o dia em que entrou. O Nasajon costuma levar de 1 a 2 dias para lançar a nota.
        </p>
      ),
    },
    {
      titulo: '2. "SOAC, pela nota" ou o nome de uma pessoa',
      conteudo: (
        <p>
          Toda chegada mostra quem a registrou. <strong>SOAC, pela nota N</strong> quer dizer que veio da nota fiscal,
          sem ninguém precisar confirmar. Quando aparece o nome de uma pessoa, foi ela quem registrou aqui.
        </p>
      ),
    },
    {
      titulo: '3. Quando você precisa agir',
      conteudo: (
        <ul>
          <li>
            A peça chegou e a nota ainda não entrou no Nasajon: abra o pedido, clique na peça e use{' '}
            <strong>Registrar chegada</strong>.
          </li>
          <li>
            A nota trouxe um item que o sistema não ligou a nenhuma peça (o fornecedor trocou o código, por exemplo): o
            pedido aparece em <strong>A identificar</strong>, e você diz a qual peça ele corresponde.
          </li>
          <li>
            Um registro está errado: <strong>Cancelar registro</strong>, com o motivo. Ele não é apagado — fica riscado
            no histórico, e a peça volta a contar como pendente.
          </li>
        </ul>
      ),
    },
    {
      titulo: '4. Completo, Parcial e a situação no Nasajon',
      conteudo: (
        <p>
          <strong>Completo</strong> quer dizer que todas as peças chegaram na quantidade pedida. Isso é contado pelas
          peças, não pela situação do pedido no Nasajon — um pedido liquidado no Nasajon pode aparecer como{' '}
          <strong>Parcial</strong> aqui, quando a nota não trouxe todas as peças. A tela do pedido mostra as duas coisas.
          Quantidade acima do pedido não some: fica marcada como excedente.
        </p>
      ),
    },
    {
      titulo: '5. Atrasado',
      conteudo: (
        <p>
          Quer dizer que a data prometida passou sem a chegada completa registrada. A peça pode ter chegado sem nota
          ainda — por isso o selo é laranja, não vermelho.
        </p>
      ),
    },
    {
      titulo: '6. Oficina externa',
      conteudo: (
        <p>
          A peça que vai direto para a oficina de terceiro conta como chegada do mesmo jeito, pela nota de entrada,
          mesmo sem passar pelo estoque. A oficina vem da requisição que originou o pedido.
        </p>
      ),
    },
    {
      titulo: '7. A aba Sincronização',
      conteudo: (
        <p>
          Mostra quando o sistema leu pela última vez os pedidos e as notas do Nasajon. Se uma chegada esperada não
          apareceu, é lá que se confere se a leitura está em dia.
        </p>
      ),
    },
    {
      titulo: '8. Quem usa esta tela',
      conteudo: (
        <p>
          O Estoque registra e corrige as chegadas. Compras, o Chefe de Oficina e a TI acompanham, sem ações.
        </p>
      ),
    },
  ];

  return (
    <div className="ConteDo recepcao-conte-do">
      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Previsão de Peças
          <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setIsHelpOpen(true)}>
            ?
          </button>
        </div>
        {abaDeTopo !== 'SINCRONIZACAO' && (
          <div className="recepcao-search-bar">
            <div className="ItemSearch" onClick={buscar} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <img src={SearchIcon} alt="Buscar" width={20} height={20} />
            </div>
            <input
              type="text"
              placeholder="Buscar por pedido, fornecedor, placa ou peça..."
              value={buscaInput}
              onChange={(e) => setBuscaInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') buscar();
              }}
              className="recepcao-search-input"
            />
          </div>
        )}
      </div>

      <div className="recepcao-tabs">
        {(
          [
            ['PEDIDOS', 'Pedidos'],
            ['PECAS', 'Peças'],
            ['SINCRONIZACAO', 'Sincronização'],
          ] as [AbaDeTopo, string][]
        ).map(([valor, rotulo]) => (
          <button
            key={valor}
            type="button"
            className={`recepcao-tab ${abaDeTopo === valor ? 'active' : ''}`}
            onClick={() => setAbaDeTopo(valor)}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {abaDeTopo !== 'SINCRONIZACAO' && (
        <div className="VisualizarOrdenar">
          <div className="CtaBig MouseHover" onClick={() => setFiltroAberto(true)}>
            <div className="Frame1000003987">
              <img src={FilterIcon} alt="Filtros" width="19.80" height="19.80" style={{ cursor: 'pointer' }} />
              <div className="ComponentName">Filtros</div>
            </div>
          </div>
        </div>
      )}

      {abaDeTopo === 'PEDIDOS' && (
        <AbaPedidos filtros={filtrosDaConsulta} versao={versao} onAbrirPedido={abrirPedido} estiloDoCard={estiloDoCardDaTabela} />
      )}
      {abaDeTopo === 'PECAS' && (
        <AbaPecas
          filtros={filtrosDasPecas}
          versao={versao}
          onAbrirItem={abrirItem}
          estiloDoCard={estiloDoCardDaTabela}
          onTrocarSubAba={setSubAbaDePecas}
        />
      )}
      {abaDeTopo === 'SINCRONIZACAO' && <SincronizacaoPrevisao />}

      {filtroAberto && (
        <FiltroPrevisaoModal
          filtrosAplicados={filtros}
          comRecebimento={abaDeTopo === 'PECAS' && subAbaDePecas === 'recebidos'}
          onAplicar={setFiltros}
          onFechar={() => setFiltroAberto(false)}
        />
      )}

      {itemAberto && (
        <PrevisaoItemModal
          item={itemAberto}
          onAbrirPedido={() => {
            const pedidoId = itemAberto.pedidoId;
            setItemAberto(null);
            abrirPedido(pedidoId);
          }}
          onFechar={() => setItemAberto(null)}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Previsão de Peças"
        secoes={secoesDaAjuda}
      />
    </div>
  );
};

export default PrevisaoPecasPage;
